import { cn } from '@/lib/utils';
import { MessageContext } from 'contexts/MessageContext';
import { useContext, useMemo, useState } from 'react';
import { useSetRecoilState } from 'recoil';

import {
  IMessageElement,
  IStep,
  messagesState,
  useChatInteract
} from '@chainlit/react-client';

import AutoResizeTextarea from '@/components/AutoResizeTextarea';
import MaterialIcon from '@/components/MaterialIcon';
import { Button } from '@/components/ui/button';
import { Translator } from 'components/i18n';

import { InlinedElements } from './Content/InlinedElements';

interface Props {
  message: IStep;
  elements: IMessageElement[];
}

export default function UserMessage({
  message,
  elements,
  children
}: React.PropsWithChildren<Props>) {
  const { askUser, loading, editable } = useContext(MessageContext);
  const { editMessage } = useChatInteract();
  const setMessages = useSetRecoilState(messagesState);
  const disabled = loading || !!askUser;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const inlineElements = useMemo(() => {
    return elements.filter(
      (el) => el.forId === message.id && el.display === 'inline'
    );
  }, [message.id, elements]);

  const handleEdit = () => {
    if (editValue) {
      setMessages((prev) => {
        const index = prev.findIndex((m) => m.id === message.id);
        if (index === -1) {
          return prev;
        }
        const slice = prev.slice(0, index + 1);
        slice[index].steps = [];
        return slice;
      });
      setIsEditing(false);
      editMessage({ ...message, output: editValue });
    }
  };

  return (
    <div className="flex gap-4 flex-row-reverse w-full">
      <div className="flex flex-col max-w-[80%] items-end">
        <InlinedElements elements={inlineElements} className="items-end" />

        <div className="flex flex-row items-center gap-1 w-full group">
          {!isEditing && editable && (
            <Button
              variant="ghost"
              size="icon"
              className="fongpt-hover edit-message ml-auto invisible group-hover:visible text-secondary"
              onClick={() => {
                setEditValue(message.output);
                setIsEditing(true);
              }}
              disabled={disabled}
            >
              <MaterialIcon name="edit" />
            </Button>
          )}
          <div
            className={cn(
              'relative rounded-[20px_20px_5px_20px] px-4 py-2.5 font-body-md text-primary fongpt-user-bubble',
              inlineElements.length ? 'rounded-tr-lg' : '',
              isEditing ? 'w-full flex-grow bg-surface border border-outline' : 'max-w-full flex-grow-0'
            )}
          >
            {isEditing ? (
              <div className="flex flex-col">
                <AutoResizeTextarea
                  id="edit-chat-input"
                  autoFocus
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="mt-1 bg-transparent placeholder:text-base placeholder:font-medium text-base text-primary"
                  maxHeight={250}
                />
                <div className="flex justify-end gap-4">
                  <Button variant="ghost" onClick={() => setIsEditing(false)}>
                    <Translator path="common.actions.cancel" />
                  </Button>
                  <Button
                    className="confirm-edit"
                    disabled={disabled}
                    onClick={handleEdit}
                  >
                    <Translator path="common.actions.confirm" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col">
                {message.command ? (
                  <div className="font-bold text-secondary command-span">
                    {message.command}
                  </div>
                ) : null}
                {children}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
